import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge, Button, Card, CardContent, Input,
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@werkstatt/ui';
import {
  Archive, AlertTriangle, Clock, EyeOff, Inbox, Link2Off, List, Loader2,
  Mail, MailOpen, Search,
} from 'lucide-react';
import { MainLayout } from '../../components/MainLayout';
import { Conversation } from './Conversation';
import api from '../../lib/api';
import type { MailboxStats, ThreadListResponse } from '../../types/mailbox';

type Filter = 'open' | 'unread' | 'failed' | 'unassigned' | 'closed' | 'hidden' | 'all';

const FILTERS: { value: Filter; label: string; icon: typeof Inbox }[] = [
  { value: 'open', label: 'Offen', icon: Inbox },
  { value: 'unread', label: 'Ungelesen', icon: Mail },
  { value: 'failed', label: 'Fehlgeschlagen', icon: AlertTriangle },
  { value: 'unassigned', label: 'Nicht zugeordnet', icon: Link2Off },
  { value: 'closed', label: 'Abgeschlossen', icon: Archive },
  { value: 'hidden', label: 'Ausgeblendet', icon: EyeOff },
  { value: 'all', label: 'Alle', icon: List },
];

function formatDateTime(value?: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function Mailbox() {
  const [filter, setFilter] = useState<Filter>('open');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<number | null>(null);

  // Tippen soll nicht bei jedem Zeichen eine Anfrage auslösen.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['mailbox-threads', filter, query],
    queryFn: async () => (await api.get<ThreadListResponse>('/mailbox/threads', {
      params: { state: filter, q: query || undefined },
    })).data,
    refetchInterval: 60_000,
  });

  const { data: stats } = useQuery({
    queryKey: ['mailbox-stats'],
    queryFn: async () => (await api.get<MailboxStats>('/mailbox/stats')).data,
    refetchInterval: 60_000,
  });

  const threads = useMemo(() => data?.threads ?? [], [data]);

  // Nach einem Filterwechsel darf kein Vorgang ausgewählt bleiben, der nicht mehr
  // in der Liste steht.
  useEffect(() => {
    if (selected !== null && !threads.some((thread) => thread.id === selected)) {
      setSelected(threads.length > 0 ? threads[0].id : null);
    } else if (selected === null && threads.length > 0) {
      setSelected(threads[0].id);
    }
  }, [threads, selected]);

  return (
    <MainLayout>
      <div className="space-y-4">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold">Postfach</h1>
            <p className="text-sm text-muted-foreground">
              Die gesamte E-Mail-Kommunikation zu Ihren Mietanfragen – lesen und
              direkt beantworten.
            </p>
          </div>
          {stats && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="secondary">
                <Inbox className="mr-1 h-3.5 w-3.5" aria-hidden />
                {stats.threads_open} offen
              </Badge>
              {stats.unread_messages > 0 && (
                <Badge>
                  <Mail className="mr-1 h-3.5 w-3.5" aria-hidden />
                  {stats.unread_messages} ungelesen
                </Badge>
              )}
              {stats.queued > 0 && (
                <Badge variant="outline">
                  <Clock className="mr-1 h-3.5 w-3.5" aria-hidden />
                  {stats.queued} in Warteschlange
                </Badge>
              )}
              {stats.failed > 0 && (
                <Badge variant="danger">
                  <AlertTriangle className="mr-1 h-3.5 w-3.5" aria-hidden />
                  {stats.failed} nicht zugestellt
                </Badge>
              )}
              {stats.unassigned > 0 && (
                <button
                  type="button"
                  onClick={() => setFilter('unassigned')}
                  className="rounded-full focus-visible:outline-none focus-visible:ring-2
                    focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <Badge variant="outline" className="cursor-pointer">
                    <Link2Off className="mr-1 h-3.5 w-3.5" aria-hidden />
                    {stats.unassigned} ohne Buchung
                  </Badge>
                </button>
              )}
            </div>
          )}
        </header>

        <div className="grid gap-4 lg:grid-cols-[minmax(280px,360px)_1fr]">
          <Card variant="glass" className="flex max-h-[calc(100vh-13rem)] flex-col overflow-hidden">
            <div className="space-y-3 border-b border-border p-3">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Name, E-Mail oder Referenz"
                  className="pl-8"
                  aria-label="Konversationen durchsuchen"
                />
              </div>
              <TooltipProvider delayDuration={200}>
                <div className="flex flex-wrap gap-1.5">
                  {FILTERS.map((item) => (
                    <Tooltip key={item.value}>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={filter === item.value ? 'default' : 'outline'}
                          onClick={() => setFilter(item.value)}
                          aria-pressed={filter === item.value}
                          aria-label={item.label}
                          className="w-8 px-0"
                        >
                          <item.icon className="h-4 w-4" aria-hidden />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{item.label}</TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </TooltipProvider>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {isLoading && (
                <div className="flex items-center justify-center py-10 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                </div>
              )}

              {!isLoading && threads.length === 0 && (
                <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                  <MailOpen className="mx-auto mb-2 h-6 w-6" aria-hidden />
                  Keine Konversationen in dieser Ansicht.
                </div>
              )}

              <ul>
                {threads.map((thread) => (
                  <li key={thread.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(thread.id)}
                      aria-current={selected === thread.id}
                      className={`w-full border-b border-border px-4 py-3 text-left transition-colors hover:bg-muted/60 ${
                        selected === thread.id ? 'bg-muted' : ''
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span
                          className={`truncate text-sm ${
                            thread.unread_count > 0 ? 'font-semibold' : 'font-medium'
                          }`}
                        >
                          {thread.customer_name || thread.customer_email}
                        </span>
                        <time className="shrink-0 text-xs text-muted-foreground">
                          {thread.last_message_at ? formatDateTime(thread.last_message_at) : ''}
                        </time>
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {thread.subject}
                      </p>
                      {thread.preview && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground/80">
                          {thread.preview}
                        </p>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {thread.unread_count > 0 && (
                          <Badge className="h-5 px-1.5 text-[11px]">
                            {thread.unread_count} neu
                          </Badge>
                        )}
                        {thread.has_failed && (
                          <Badge variant="danger" className="h-5 px-1.5 text-[11px]">
                            Zustellfehler
                          </Badge>
                        )}
                        {thread.is_closed && (
                          <Badge variant="outline" className="h-5 px-1.5 text-[11px]">
                            abgeschlossen
                          </Badge>
                        )}
                        {thread.is_hidden && (
                          <Badge variant="outline" className="h-5 px-1.5 text-[11px]">
                            ausgeblendet
                          </Badge>
                        )}
                        <span className="text-[11px] text-muted-foreground">
                          {thread.message_count} Nachricht(en)
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </Card>

          <Card variant="glass" className="max-h-[calc(100vh-13rem)] overflow-hidden">
            {selected === null ? (
              <CardContent className="flex h-full min-h-[300px] items-center justify-center text-center text-sm text-muted-foreground">
                Wählen Sie links eine Konversation aus.
              </CardContent>
            ) : (
              <Conversation threadId={selected} onDeleted={() => setSelected(null)} />
            )}
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
